import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin, Check, Music2, Linkedin } from "lucide-react";
import { Reveal, MaskRevealInView } from "../components/Reveal";
import { CONTACT, CONTACT_COPY } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus("error");
      setFeedback("Please fill in your name, email and message.");
      return;
    }
    setStatus("loading");
    setFeedback("");
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setStatus("success");
      setFeedback(data.message || "Thank you — your message is on its way.");
      setForm({ name: "", email: "", business_name: "", message: "" });
    } catch (err) {
      setStatus("error");
      setFeedback("Something went wrong. Please email me directly at " + CONTACT.email);
    }
  };

  return (
    <section className="pt-36 md:pt-48 pb-24 md:pb-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <Reveal>
          <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-rosedeep mb-10">
            Contact — Let's talk
          </p>
        </Reveal>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.0] text-ink tracking-tight max-w-4xl">
          <MaskRevealInView lines={["Let's build something"]} />
          <span className="italic font-display text-rosedeep">
            <MaskRevealInView lines={["meaningful."]} delay={0.15} />
          </span>
        </h1>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Details */}
          <div className="lg:col-span-4">
            <Reveal>
              <p className="font-sans text-lg leading-relaxed text-graphite max-w-sm">
                {CONTACT_COPY.copy}
              </p>
              <div className="mt-12 space-y-6">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-borderwarm text-ink transition-colors duration-300 group-hover:bg-rosedeep group-hover:text-white group-hover:border-rosedeep">
                    <Mail size={16} />
                  </span>
                  <span className="font-sans text-sm text-graphite hover-underline break-all">{CONTACT.email}</span>
                </a>
                <a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-borderwarm text-ink transition-colors duration-300 group-hover:bg-rosedeep group-hover:text-white group-hover:border-rosedeep">
                    <Phone size={16} />
                  </span>
                  <span className="font-sans text-sm text-graphite hover-underline">{CONTACT.phone}</span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-borderwarm text-ink">
                    <MapPin size={16} />
                  </span>
                  <span className="font-sans text-sm text-graphite">{CONTACT.location}</span>
                </div>
                <a href={CONTACT.tiktokUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center border border-borderwarm text-ink transition-colors duration-300 group-hover:bg-rosedeep group-hover:text-white group-hover:border-rosedeep">
                    <Music2 size={16} />
                  </span>
                  <span className="font-sans text-sm text-graphite hover-underline">TikTok {CONTACT.tiktok}</span>
                </a>
                {CONTACT.linkedinUrl && (
                  <a href={CONTACT.linkedinUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-borderwarm text-ink transition-colors duration-300 group-hover:bg-rosedeep group-hover:text-white group-hover:border-rosedeep">
                      <Linkedin size={16} />
                    </span>
                    <span className="font-sans text-sm text-graphite hover-underline">LinkedIn — {CONTACT.linkedin}</span>
                  </a>
                )}
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            {status === "success" ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-borderwarm bg-blush p-12 lg:p-16 h-full flex flex-col justify-center"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-rosedeep text-white mb-8">
                  <Check size={22} />
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-ink">Thank you.</h2>
                <p className="mt-4 font-sans text-lg text-mutedfg max-w-md">{feedback}</p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setStatus("idle")}
                  className="mt-8 self-start font-sans text-[12px] tracking-[0.16em] uppercase text-ink hover-underline"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-mutedfg mb-3">Name</label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                      className="w-full bg-transparent border-b border-borderwarm py-3 font-sans text-lg text-ink placeholder:text-mutedfg/60 focus:border-rosedeep focus:outline-none transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-mutedfg mb-3">Email</label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className="w-full bg-transparent border-b border-borderwarm py-3 font-sans text-lg text-ink placeholder:text-mutedfg/60 focus:border-rosedeep focus:outline-none transition-colors duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-mutedfg mb-3">Business Name</label>
                  <input
                    data-testid="contact-business"
                    type="text"
                    value={form.business_name}
                    onChange={update("business_name")}
                    placeholder="Your business (optional)"
                    className="w-full bg-transparent border-b border-borderwarm py-3 font-sans text-lg text-ink placeholder:text-mutedfg/60 focus:border-rosedeep focus:outline-none transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="block font-sans text-[11px] tracking-[0.2em] uppercase text-mutedfg mb-3">How Can I Help?</label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell me about your business and how I can support you…"
                    className="w-full bg-transparent border-b border-borderwarm py-3 font-sans text-lg text-ink placeholder:text-mutedfg/60 focus:border-rosedeep focus:outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                {status === "error" && (
                  <p data-testid="contact-error" className="font-sans text-sm text-destructive">{feedback}</p>
                )}

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={status === "loading"}
                  className="group inline-flex items-center gap-3 rounded-full bg-rosedeep text-white px-10 py-4 font-sans text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 hover:bg-ink disabled:opacity-60"
                >
                  {status === "loading" ? "Sending…" : "Submit"}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
